import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Animated } from 'react-native';
import {
  Box,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  isTrackReference,
  useLocalParticipant,
  useParticipants,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import {
  OralExamSupervisorStatus,
  type OralExamSupervisorStatus as OralExamSupervisorStatusType,
} from '@/common/api/models/oralExamSupervisorStatus';
import { resolveLecturerCameraTrack } from '@/common/components/oralExam/BriefingConferenceStage';
import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';
import {
  LabStandInImage,
  useOralExamLabOverrides,
} from '@/common/oralExam/oralExamLabOverrides';

import {
  isSupervisorWellLive,
  resolveSupervisorStatusLabel,
  shouldShowSupervisorWell,
  SupervisorPortrait,
} from './SupervisorPresence';
import { SELF_VIEW_CHIP_INSET_PX, SELF_VIEW_CHIP_WIDTH_PX } from './selfViewTile';

/** Calm 4:3 frame width — constant across Watching and Appear. */
export const SUPERVISOR_FRAME_WIDTH_PX = 240;
/** Small screens: the calm frame matches the student's own self-view tile. */
export const SUPERVISOR_FRAME_COMPACT_WIDTH_PX = SELF_VIEW_CHIP_WIDTH_PX;
export const SUPERVISOR_FRAME_COMPACT_INSET_PX = SELF_VIEW_CHIP_INSET_PX;
/** Engaged expansion width; gutter is reserved from this value in every rung. */
export const SUPERVISOR_FRAME_ENGAGED_WIDTH_PX = 360;
export const SUPERVISOR_FRAME_ENGAGED_HEIGHT_PX = 270;
export const SUPERVISOR_FRAME_INSET_PX = 24;
export const QUESTION_COLUMN_PT = 50;
/** Cohort group-exam question column top inset; 1:1 keeps {@link QUESTION_COLUMN_PT}. */
export const COHORT_QUESTION_COLUMN_PT = 24;
export const QUESTION_COLUMN_PR_WATCHING_PX = SUPERVISOR_FRAME_INSET_PX
  + SUPERVISOR_FRAME_WIDTH_PX
  + SUPERVISOR_FRAME_INSET_PX;
export const QUESTION_COLUMN_PR_ENGAGED_PX = SUPERVISOR_FRAME_INSET_PX
  + SUPERVISOR_FRAME_ENGAGED_WIDTH_PX
  + SUPERVISOR_FRAME_INSET_PX;
export const QUESTION_COLUMN_PR_COMPACT_PX = SUPERVISOR_FRAME_COMPACT_INSET_PX
  + SUPERVISOR_FRAME_COMPACT_WIDTH_PX
  + SUPERVISOR_FRAME_COMPACT_INSET_PX;
/** Legacy 1:1 gutter — always reserves the engaged frame width. */
export const QUESTION_COLUMN_PR_PX = QUESTION_COLUMN_PR_ENGAGED_PX;

export function resolveCohortQuestionColumnPrPx(
  status: OralExamSupervisorStatusType,
  compact = false,
): number {
  if (status === OralExamSupervisorStatus.engaged) return QUESTION_COLUMN_PR_ENGAGED_PX;
  return compact ? QUESTION_COLUMN_PR_COMPACT_PX : QUESTION_COLUMN_PR_WATCHING_PX;
}

const SUPERVISOR_WELL_CROSSFADE_MS = 600;
const SUPERVISOR_WELL_EXPAND_MS = 320;
const SUPERVISOR_WELL_STILL_COLOR = '#E4E7EC';
const SUPERVISOR_NAME_BAND_COLOR = 'rgba(17, 24, 39, 0.55)';
const SUPERVISOR_FACE_GAP_PX = 2;

const FILL_VIDEO_STYLE = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%' as const,
  height: '100%' as const,
  objectFit: 'cover' as const,
};

export type SupervisorFrameLayoutPhase = 'question_active' | 'hold';

export type SupervisorFaceDisplay = {
  lecturerId: string;
  name: string;
  photoUrl?: string | number | { uri?: string } | null;
};

export type SupervisorFrameProps = {
  supervisorName: string;
  supervisorPhotoUrl?: string | number | { uri?: string } | null;
  /** At-rest faces from the server view (max four). Ignored during Appear/Engage. */
  supervisorFaces?: SupervisorFaceDisplay[];
  /** Visiting supervisor LiveKit identity prefix when Appear/Engage is active. */
  visitingLecturerId?: string | null;
  bookingId?: string;
  status: OralExamSupervisorStatusType;
  layoutPhase: SupervisorFrameLayoutPhase;
  /** Top inset aligned with the question column; cohort question_active uses {@link COHORT_QUESTION_COLUMN_PT}. */
  questionColumnTopInset?: number;
  /** Relocated lecturer_pause cue when Engaged (banner suppressed in shell). */
  showEngagedPauseCue?: boolean;
  /**
   * Small screens: the calm frame shrinks to the self-view tile size with the
   * name and status in an overlay band. Engaged keeps its full expansion.
   */
  compact?: boolean;
};

/** True when the lecturer camera track is live, unmuted, and not ended. */
export function hasSupervisorLiveVideo(
  trackRef: ReturnType<typeof resolveLecturerCameraTrack>,
): boolean {
  if (!trackRef || !isTrackReference(trackRef)) return false;
  const { track, isMuted } = trackRef.publication;
  if (!track || isMuted) return false;
  const readyState = track.mediaStreamTrack?.readyState;
  return readyState !== 'ended';
}

function resolveTargetFrameWidth(status: OralExamSupervisorStatusType, compact: boolean): number {
  if (status === OralExamSupervisorStatus.engaged) return SUPERVISOR_FRAME_ENGAGED_WIDTH_PX;
  return compact ? SUPERVISOR_FRAME_COMPACT_WIDTH_PX : SUPERVISOR_FRAME_WIDTH_PX;
}

function examSupervisorIdentity(lecturerId: string, bookingId: string): string {
  return `${lecturerId}:${bookingId}:lecturer`;
}

/** Last whitespace token. "Prof. Severus Snape" → "Snape". */
export function lecturerLastName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : name;
}

function resolveVisitingSupervisorCameraTrack(
  cameraTracks: ReturnType<typeof useTracks>,
  participants: ReturnType<typeof useParticipants>,
  visitingLecturerId: string | null | undefined,
  bookingId: string | undefined,
) {
  if (!visitingLecturerId || !bookingId) {
    return undefined;
  }
  const identity = examSupervisorIdentity(visitingLecturerId, bookingId);
  const byIdentity = cameraTracks.find(
    (trackRef) => trackRef.participant.identity === identity,
  );
  if (byIdentity) return byIdentity;
  const participant = participants.find((p) => p.identity === identity);
  if (participant) {
    return { participant, source: Track.Source.Camera };
  }
  return undefined;
}

function liveCameraForLecturer(
  cameraTracks: ReturnType<typeof useTracks>,
  lecturerId: string,
  bookingId: string | undefined,
) {
  if (!bookingId) return undefined;
  const identity = examSupervisorIdentity(lecturerId, bookingId);
  return cameraTracks.find((trackRef) => trackRef.participant.identity === identity);
}

function SupervisorLiveVideo({
  track,
  testID,
}: {
  track: ReturnType<typeof resolveVisitingSupervisorCameraTrack>;
  testID: string;
}) {
  if (!track || !hasSupervisorLiveVideo(track) || !isTrackReference(track)) return null;
  return (
    <Box
      testID={testID}
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      pointerEvents="none"
    >
      <VideoTrack trackRef={track} style={FILL_VIDEO_STYLE} />
    </Box>
  );
}

function SupervisorFaceCell({
  face,
  flex,
  testId,
  liveTrack,
  showName,
}: {
  face: SupervisorFaceDisplay;
  flex?: number;
  testId?: string;
  liveTrack?: ReturnType<typeof resolveVisitingSupervisorCameraTrack>;
  showName: boolean;
}) {
  const lastName = lecturerLastName(face.name);
  return (
    <Box
      testID={testId}
      flex={flex ?? 1}
      aspectRatio={1}
      overflow="hidden"
      bg={SUPERVISOR_WELL_STILL_COLOR}
    >
      <SupervisorPortrait
        name={face.name}
        photoUrl={face.photoUrl}
        fill
      />
      <SupervisorLiveVideo
        track={liveTrack}
        testID={`oral-exam-supervisor-live-${face.lecturerId}`}
      />
      {showName ? (
        <Box
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          px="$1.5"
          py="$1"
          style={{ backgroundColor: SUPERVISOR_NAME_BAND_COLOR }}
        >
          <Text
            testID={testId ? `oral-exam-supervisor-lastname-${testId.replace('oral-exam-supervisor-face-', '')}` : 'oral-exam-supervisor-lastname'}
            size="xs"
            color="$white"
            textAlign="center"
            numberOfLines={1}
            fontWeight="$medium"
          >
            {lastName}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

function SupervisorFacesGrid({
  faces,
  liveTrackFor,
  showNames,
}: {
  faces: SupervisorFaceDisplay[];
  liveTrackFor: (lecturerId: string) => ReturnType<typeof resolveVisitingSupervisorCameraTrack>;
  showNames: boolean;
}) {
  if (faces.length === 1) {
    return (
      <Box flex={1} width="100%" height="100%">
        <SupervisorPortrait
          name={faces[0].name}
          photoUrl={faces[0].photoUrl}
          fill
        />
        <SupervisorLiveVideo
          track={liveTrackFor(faces[0].lecturerId)}
          testID={`oral-exam-supervisor-live-${faces[0].lecturerId}`}
        />
      </Box>
    );
  }

  if (faces.length === 2) {
    return (
      <HStack flex={1} width="100%" height="100%" space="xs">
        {faces.map((face, index) => (
          <SupervisorFaceCell
            key={face.lecturerId}
            face={face}
            testId={`oral-exam-supervisor-face-${index}`}
            liveTrack={liveTrackFor(face.lecturerId)}
            showName={showNames}
          />
        ))}
      </HStack>
    );
  }

  const cells = faces.slice(0, 4);
  const rows = [
    cells.slice(0, 2),
    cells.slice(2, 4),
  ];

  return (
    <VStack flex={1} width="100%" height="100%" space="xs">
      {rows.map((row) => (
        row.length > 0 ? (
          <HStack key={row.map((face) => face.lecturerId).join('-')} flex={1} space="xs">
            {row.map((face, index) => (
              <SupervisorFaceCell
                key={face.lecturerId}
                face={face}
                testId={`oral-exam-supervisor-face-${face.lecturerId}-${index}`}
                liveTrack={liveTrackFor(face.lecturerId)}
                showName={showNames}
              />
            ))}
            {row.length === 1 ? <Box flex={1} /> : null}
          </HStack>
        ) : null
      ))}
    </VStack>
  );
}

/**
 * SupervisorFrame
 * ----
 * Fixed 4:3 supervision widget floated top-right inside the exam phase body.
 * At rest shows up to four supervisor faces. A lecturer who is observing
 * replaces their own photo with their camera; both do if both are looking in.
 * Appear/Engage covers the grid with the visiting supervisor's camera.
 */
export function SupervisorFrame({
  supervisorName,
  supervisorPhotoUrl,
  supervisorFaces = [],
  visitingLecturerId,
  bookingId,
  status,
  layoutPhase,
  questionColumnTopInset = QUESTION_COLUMN_PT,
  showEngagedPauseCue = false,
  compact = false,
}: SupervisorFrameProps) {
  const { t } = useTranslation();
  const lab = useOralExamLabOverrides();
  const reducedMotion = useOrbReducedMotion();
  const liveOpacity = useRef(new Animated.Value(0)).current;
  const frameWidth = useRef(new Animated.Value(SUPERVISOR_FRAME_WIDTH_PX)).current;
  const prevStatusRef = useRef<OralExamSupervisorStatusType | null>(null);
  const prevHasLiveVideoRef = useRef<boolean | null>(null);
  const [retainLiveLayer, setRetainLiveLayer] = useState(false);
  const [syncedHasLiveVideo, setSyncedHasLiveVideo] = useState(false);
  const statusLabel = resolveSupervisorStatusLabel(status, t);
  const wellLive = isSupervisorWellLive(status);
  const targetWidth = resolveTargetFrameWidth(status, compact);
  const compactTile = compact && status !== OralExamSupervisorStatus.engaged;
  const atRestFaces = useMemo(
    () => (wellLive ? [] : supervisorFaces),
    [supervisorFaces, wellLive],
  );
  const useMultiFaceLayout = !wellLive && atRestFaces.length > 1;
  const singleAtRestFace = !wellLive && atRestFaces.length === 1 ? atRestFaces[0] : null;

  const headlineName = wellLive
    ? supervisorName
    : (atRestFaces.length >= 2
      ? t('oralExam.supervisor.yourSupervisors', { defaultValue: 'Your supervisors' })
      : (singleAtRestFace?.name ?? supervisorName));

  const stillPhotoUrl = wellLive
    ? (lab?.supervisorStillImageUrl ?? supervisorPhotoUrl)
    : (singleAtRestFace?.photoUrl ?? lab?.supervisorStillImageUrl ?? supervisorPhotoUrl);

  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: true },
  );

  const liveTrackForFace = useMemo(() => {
    const byLecturer = new Map<string, ReturnType<typeof liveCameraForLecturer>>();
    atRestFaces.forEach((face) => {
      byLecturer.set(
        face.lecturerId,
        liveCameraForLecturer(cameraTracks, face.lecturerId, bookingId),
      );
    });
    return (lecturerId: string) => byLecturer.get(lecturerId);
  }, [atRestFaces, bookingId, cameraTracks]);

  const lecturerTrack = useMemo(
    () => {
      if (wellLive && visitingLecturerId) {
        return resolveVisitingSupervisorCameraTrack(
          cameraTracks,
          participants,
          visitingLecturerId,
          bookingId,
        ) ?? resolveLecturerCameraTrack(
          cameraTracks,
          participants,
          localParticipant,
          'student',
        );
      }
      return resolveLecturerCameraTrack(
        cameraTracks,
        participants,
        localParticipant,
        'student',
      );
    },
    [bookingId, cameraTracks, localParticipant, participants, visitingLecturerId, wellLive],
  );

  const labLiveUrl = lab?.supervisorLiveImageUrl ?? null;
  const hasLabLive = wellLive && labLiveUrl != null;
  const hasLiveVideo = hasLabLive || (wellLive && hasSupervisorLiveVideo(lecturerTrack));

  if (hasLiveVideo !== syncedHasLiveVideo) {
    const wasShowingLiveVideo = syncedHasLiveVideo;
    setSyncedHasLiveVideo(hasLiveVideo);
    if (hasLiveVideo) {
      setRetainLiveLayer(false);
    } else if (wasShowingLiveVideo && isTrackReference(lecturerTrack)) {
      setRetainLiveLayer(true);
    }
  }

  const showLiveLayer = hasLabLive
    || ((hasLiveVideo || retainLiveLayer) && isTrackReference(lecturerTrack));

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const prevHasLiveVideo = prevHasLiveVideoRef.current;
    prevStatusRef.current = status;
    prevHasLiveVideoRef.current = hasLiveVideo;

    const snapAll = () => {
      frameWidth.setValue(targetWidth);
      liveOpacity.setValue(hasLiveVideo ? 1 : 0);
    };

    const runWidth = (toValue: number, onComplete?: () => void) => {
      if (reducedMotion) {
        frameWidth.setValue(toValue);
        onComplete?.();
        return;
      }
      Animated.timing(frameWidth, {
        toValue,
        duration: SUPERVISOR_WELL_EXPAND_MS,
        useNativeDriver: false,
      }).start(({ finished }) => {
        if (finished) onComplete?.();
      });
    };

    const runLiveFade = (toValue: number, onComplete?: () => void) => {
      if (reducedMotion) {
        liveOpacity.setValue(toValue);
        onComplete?.();
        return;
      }
      Animated.timing(liveOpacity, {
        toValue,
        duration: SUPERVISOR_WELL_CROSSFADE_MS,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) onComplete?.();
      });
    };

    const completeLiveLayerFadeOut = (onComplete?: () => void) => {
      runLiveFade(0, () => {
        setRetainLiveLayer(false);
        onComplete?.();
      });
    };

    if (prevStatus === null) {
      snapAll();
      return undefined;
    }

    if (reducedMotion) {
      snapAll();
      return undefined;
    }

    const wasEngaged = prevStatus === OralExamSupervisorStatus.engaged;
    const nowEngaged = status === OralExamSupervisorStatus.engaged;
    const wasLive = isSupervisorWellLive(prevStatus);
    const nowLive = isSupervisorWellLive(status);

    if (nowEngaged && !wasEngaged) {
      if (wasLive) {
        runWidth(targetWidth);
      } else {
        runLiveFade(1, () => {
          runWidth(targetWidth);
        });
      }
      return undefined;
    }

    if (wasEngaged && !nowEngaged) {
      runWidth(targetWidth, () => {
        if (!nowLive && isTrackReference(lecturerTrack)) {
          completeLiveLayerFadeOut();
        }
      });
      return undefined;
    }

    if (nowLive && !wasLive) {
      runLiveFade(1);
      return undefined;
    }

    if (!nowLive && wasLive) {
      completeLiveLayerFadeOut();
      return undefined;
    }

    if (prevStatus === status) {
      frameWidth.setValue(targetWidth);
      if (hasLiveVideo && prevHasLiveVideo === false) {
        runLiveFade(1);
        return undefined;
      }
      if (!hasLiveVideo && prevHasLiveVideo === true && isTrackReference(lecturerTrack)) {
        completeLiveLayerFadeOut();
        return undefined;
      }
      return undefined;
    }

    frameWidth.setValue(targetWidth);
    if (!hasLiveVideo) {
      liveOpacity.setValue(0);
    }

    return undefined;
  }, [
    frameWidth,
    hasLiveVideo,
    lecturerTrack,
    liveOpacity,
    reducedMotion,
    status,
    targetWidth,
  ]);

  if (!shouldShowSupervisorWell(status)) {
    return null;
  }

  const anchorTop = layoutPhase === 'question_active'
    ? questionColumnTopInset
    : SUPERVISOR_FRAME_INSET_PX;
  const anchorRight = compact ? SUPERVISOR_FRAME_COMPACT_INSET_PX : SUPERVISOR_FRAME_INSET_PX;

  const nameText = (
    <Text
      testID="oral-exam-supervisor-name"
      size={compactTile ? '2xs' : 'sm'}
      fontWeight="semibold"
      color={compactTile ? '$textInverted' : '$textHeading'}
      numberOfLines={1}
      flexShrink={compactTile ? 1 : undefined}
    >
      {compactTile && atRestFaces.length < 2 ? lecturerLastName(headlineName) : headlineName}
    </Text>
  );
  const statusText = (
    <Text
      testID="oral-exam-supervisor-status"
      size={compactTile ? '2xs' : 'xs'}
      color={compactTile ? '$textInverted' : '$textSubtle'}
      accessibilityLabel={statusLabel}
      numberOfLines={1}
      flexShrink={0}
    >
      {compactTile ? `· ${statusLabel}` : statusLabel}
    </Text>
  );

  const presenceBody = (
    <VStack
      testID="oral-exam-supervisor-presence"
      width="100%"
      alignItems="stretch"
      space="xs"
      flexShrink={0}
    >
      <Box
        testID="oral-exam-supervisor-well"
        width="100%"
        aspectRatio={4 / 3}
        borderWidth={1}
        borderColor="$borderLight"
        borderRadius={compactTile ? '$md' : '$sm'}
        overflow="hidden"
        position="relative"
        bg={SUPERVISOR_WELL_STILL_COLOR}
        p={useMultiFaceLayout ? '$0' : undefined}
        style={useMultiFaceLayout ? { gap: SUPERVISOR_FACE_GAP_PX } : undefined}
      >
        {useMultiFaceLayout ? (
          <Box flex={1} width="100%" height="100%" testID="oral-exam-supervisor-faces-grid">
            <SupervisorFacesGrid
              faces={atRestFaces}
              liveTrackFor={liveTrackForFace}
              showNames={!compactTile}
            />
          </Box>
        ) : (
          <Box flex={1} width="100%" height="100%">
            <SupervisorPortrait
              name={wellLive ? supervisorName : (singleAtRestFace?.name ?? supervisorName)}
              photoUrl={stillPhotoUrl}
              fill
            />
            {!wellLive && singleAtRestFace ? (
              <SupervisorLiveVideo
                track={liveTrackForFace(singleAtRestFace.lecturerId)}
                testID={`oral-exam-supervisor-live-${singleAtRestFace.lecturerId}`}
              />
            ) : null}
          </Box>
        )}
        {showLiveLayer ? (
          <Animated.View
            pointerEvents="none"
            testID="oral-exam-supervisor-video"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: liveOpacity,
            }}
          >
            {hasLabLive && labLiveUrl != null ? (
              <LabStandInImage
                source={labLiveUrl}
                testID="patrol-lab-standin-supervisor-live"
              />
            ) : isTrackReference(lecturerTrack) ? (
              <VideoTrack trackRef={lecturerTrack} style={FILL_VIDEO_STYLE} />
            ) : null}
          </Animated.View>
        ) : null}
        {showEngagedPauseCue ? (
          <Box
            testID="oral-exam-supervisor-engaged-pause-cue"
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            px="$2"
            py="$1"
            bg="rgba(16, 24, 40, 0.72)"
          >
            <Text size="xs" color="$textInverted" numberOfLines={2}>
              {t('oralExam.supervisor.engagedPauseCue', {
                defaultValue: 'Exam is paused. Nothing you say now is part of the exam.',
              })}
            </Text>
          </Box>
        ) : null}
        {compactTile ? (
          <HStack
            testID="oral-exam-supervisor-compact-band"
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bg="$bgDark"
            opacity={0.85}
            px="$2"
            py={3}
            space="xs"
            alignItems="center"
          >
            {nameText}
            {statusText}
          </HStack>
        ) : null}
      </Box>
      {compactTile ? null : (
        <>
          {nameText}
          {statusText}
        </>
      )}
    </VStack>
  );

  return (
    <Box
      testID="oral-exam-supervisor-frame-anchor"
      position="absolute"
      top={anchorTop}
      right={anchorRight}
      zIndex={2}
    >
      {reducedMotion ? (
        <Box
          testID="oral-exam-supervisor-frame"
          width={targetWidth}
          alignSelf="flex-end"
        >
          {presenceBody}
        </Box>
      ) : (
        <Animated.View
          testID="oral-exam-supervisor-frame"
          style={{ width: frameWidth, alignSelf: 'flex-end' }}
        >
          {presenceBody}
        </Animated.View>
      )}
    </Box>
  );
}
